import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { user } from '../../auth'

// =============================================================================
// READING JOURNAL - Lookup table (generated by background function)
// Stores aggregated reading journal data by user, week, and day of week
// =============================================================================

export const ReadingJournal = pgTable(
  'reading_journal',
  {
    id: uuid().defaultRandom().primaryKey(),
    userId: text()
      .references(() => user.id, { onDelete: 'cascade' })
      .notNull(),
    weekStartDate: timestamp({ mode: 'date', withTimezone: true }).notNull(), // Start date of the week (Monday)
    dayOfWeek: integer().notNull(), // Day of week: 0=Sunday, 1=Monday, ..., 6=Saturday
    totalDurationSeconds: integer().notNull().default(0),
    sessionCount: integer().notNull().default(0),
    // --- Membership snapshot (the "Laporan" dimension) --------------------------------------
    // The school context the learner was in AT ROLL-UP TIME, stamped by the producer on every write.
    // A report ("Laporan baca kelas 1 A") groups on these, so it keeps meaning the members of 1 A in
    // that period even after a student is moved to another class or promoted to the next grade.
    // Every column is nullable: a child who only belongs to a family organization has no school
    // context. A child can be a member of two organizations at once (family + school), which is
    // exactly why the organization is recorded here instead of being re-derived at read time.
    // NOT FOREIGN KEYS, deliberately. A snapshot must carry no referential dependency at all: even
    // `ON DELETE SET NULL` erases the value the moment a school or a class is deleted, which is the
    // history loss the snapshot exists to prevent. Plain text, resolved by the producer via
    // resolveMetricSnapshotContexts (packages/drizzle/src/queries/metric-snapshot-context.ts).
    // Because there is no FK, an id on its own is unrenderable once the row it pointed at is gone,
    // so the labels are stored beside it. They are stamped at roll-up time like everything else
    // here, which means a rebuild re-stamps them too — a renamed class rewrites its own history.
    schoolOrganizationId: text(),
    // The school's name at roll-up time, mirroring organization.name.
    schoolOrganizationName: text(),
    // The "Kelas" / rombel. Always a team of schoolOrganizationId above.
    schoolTeamId: text(),
    // The class label a report prints ("1 A"), mirroring team.name.
    schoolTeamName: text(),
    // The class avatar URL, mirroring team.image.
    schoolTeamImage: text(),
    // The "Tingkat" / grade, mirroring user.schoolClass — a plain text column, not a DB enum.
    // No separate label: it already IS the human-readable value, not an id.
    schoolClass: text(),
    // The learner's own name and avatar at roll-up time, mirroring user.name / user.image.
    // Snapshotted for the same reason as the class label: a learner can be renamed or deleted.
    studentName: text(),
    studentImage: text(),

    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_reading_journal_user').on(t.userId),
    index('idx_reading_journal_week_start').on(t.weekStartDate),
    index('idx_reading_journal_user_week_day').on(t.userId, t.weekStartDate, t.dayOfWeek),
    // Reporting reads: every row a school produced in a period, then narrowed to one class.
    index('idx_reading_journal_school_org_week').on(t.schoolOrganizationId, t.weekStartDate),
    index('idx_reading_journal_school_team_week').on(t.schoolTeamId, t.weekStartDate),
    unique('uq_reading_journal_user_week_day').on(t.userId, t.weekStartDate, t.dayOfWeek),
  ],
)

export const readingJournalRelations = relations(ReadingJournal, ({ one }) => ({
  user: one(user, {
    fields: [ReadingJournal.userId],
    references: [user.id],
  }),
}))

export const InsertReadingJournalSchema = createInsertSchema(ReadingJournal).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateReadingJournalSchema = createUpdateSchema(ReadingJournal).partial().omit({
  id: true,
  userId: true,
  weekStartDate: true,
  dayOfWeek: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectReadingJournalSchema = createSelectSchema(ReadingJournal)

export type InsertReadingJournal = typeof ReadingJournal.$inferInsert
export type UpdateReadingJournal = z.infer<typeof UpdateReadingJournalSchema>
export type SelectReadingJournal = typeof ReadingJournal.$inferSelect
